// ===========================================
// Access-token refresh: single-flight, and only a REFUSED refresh ends the session (#524)
// ===========================================
//
// The backend ROTATES refresh tokens: a refresh deletes the token it was given, so a second
// refresh with the same token is refused (401 "Token has been revoked."). When every request
// that 401'd ran its own refresh, two reads on an expired token raced, the loser was refused, and
// its error handler cleared the WINNER's freshly stored tokens and bounced the user to login.
//
// So:
// - ONE refresh per tab at a time (`refreshPromise`), and one per browser where Web Locks exist:
//   tabs share localStorage, so a tab that waited for the lock first checks whether another tab
//   already refreshed.
// - Without Web Locks (Safari < 15.4, or the lock API failing), two tabs can still race. The
//   loser's 401 usually lands BEFORE the winner's 200 (the server deletes the old token, then
//   creates the new one, then answers), so a refused refresh that was not serialised waits
//   `REFUSAL_GRACE_MS` for another tab to store a new pair before calling the session dead.
// - A refresh refused by the server (a 4xx) ends the session. One that got no answer (offline,
//   timeout, 5xx, 408, 429, a malformed body) does NOT: the caller keeps its tokens and its
//   request just fails.
// - A refresh that completes after a logout never writes the dead session back.
//
// The caller (`services/api.ts`) clears storage and redirects. This module also clears a REFUSED
// pair itself, under the lock, so a tab waiting for the lock does not re-send it.

import axios, { type AxiosError } from 'axios';

const REFRESH_LOCK = 'mnm-auth-refresh';
/** A hung refresh would otherwise hold the cross-tab lock, and every waiting request, forever. */
export const REFRESH_TIMEOUT_MS = 15_000;
/** How long a tab waits for the lock before refreshing without it (a frozen holder). */
export const LOCK_WAIT_MS = REFRESH_TIMEOUT_MS + 5_000;
/** How long an unserialised refused refresh waits for another tab's new pair. */
export const REFUSAL_GRACE_MS = 2_000;
const GRACE_POLL_MS = 100;

const sessionEndedErrors = new WeakSet<object>();

function sessionEnded<E extends object>(error: E): E {
  sessionEndedErrors.add(error);
  return error;
}

/** True when the refresh was refused (or impossible): the stored session is dead. */
export function isSessionEnded(error: unknown): boolean {
  return typeof error === 'object' && error !== null && sessionEndedErrors.has(error);
}

/** A 4xx is the server refusing the token. 408 and 429 are the server not answering it. */
function isRefusal(error: unknown): error is AxiosError {
  if (!axios.isAxiosError(error) || !error.response) return false;
  const { status } = error.response;
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/** Another tab rotated the token we sent and stored the new pair: use it. */
function rotatedElsewhere(sent: string): string | null {
  const storedRefresh = localStorage.getItem('refreshToken');
  const storedAccess = localStorage.getItem('accessToken');
  return storedRefresh && storedRefresh !== sent && storedAccess ? storedAccess : null;
}

async function waitForRotationElsewhere(sent: string): Promise<string | null> {
  const deadline = Date.now() + REFUSAL_GRACE_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, GRACE_POLL_MS));
    const rotated = rotatedElsewhere(sent);
    if (rotated) return rotated;
  }
  return null;
}

/**
 * Run `fn` holding the cross-tab lock. `fn` is told whether it is serialised: if the lock API is
 * missing, fails, or is held too long, it runs anyway, unserialised, rather than hang or never
 * refresh again.
 */
async function withRefreshLock<T>(fn: (serialised: boolean) => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return fn(false);

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), LOCK_WAIT_MS);
  let granted = false;
  try {
    return await locks.request(REFRESH_LOCK, { signal: abort.signal }, () => {
      granted = true;
      clearTimeout(timer);
      return fn(true);
    });
  } catch (error) {
    if (granted) throw error; // fn's own rejection
    return fn(false);
  } finally {
    clearTimeout(timer);
  }
}

async function runRefresh(refreshUrl: string, staleAccess: string | null, serialised: boolean): Promise<string> {
  // Another tab refreshed while this one waited for the lock.
  const storedAccess = localStorage.getItem('accessToken');
  if (storedAccess && storedAccess !== staleAccess) return storedAccess;

  const sent = localStorage.getItem('refreshToken');
  if (!sent) throw sessionEnded(new Error('No refresh token'));

  let tokens: { accessToken: string; refreshToken: string };
  try {
    const response = await axios.post(refreshUrl, { refreshToken: sent }, { timeout: REFRESH_TIMEOUT_MS });
    // Backend returns { status: 'success', data: { accessToken, refreshToken } }
    tokens = response.data?.data;
    if (typeof tokens?.accessToken !== 'string' || typeof tokens?.refreshToken !== 'string') {
      throw new Error('Malformed refresh response');
    }
  } catch (error) {
    // Lost the race to another tab: its pair may already be stored, or about to be.
    const rotated = rotatedElsewhere(sent) ?? (isRefusal(error) && !serialised ? await waitForRotationElsewhere(sent) : null);
    if (rotated) return rotated;
    if (!isRefusal(error)) throw error;
    // Clear the refused pair while still holding the lock (only if it is still the one stored),
    // or the next tab to take the lock re-sends it before this tab's caller clears it.
    if (localStorage.getItem('refreshToken') === sent) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
    // Rethrow the AxiosError itself, marked, so callers still see its status.
    throw sessionEnded(error);
  }

  // Compare-and-set: write only over the session this refresh was for.
  const storedRefresh = localStorage.getItem('refreshToken');
  if (storedRefresh === sent) {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    return tokens.accessToken;
  }
  const current = rotatedElsewhere(sent);
  if (current) return current;
  // Logged out while the refresh was in flight.
  throw sessionEnded(new Error('Session ended'));
}

let refreshPromise: Promise<string> | null = null;

/**
 * Refresh the access token, sharing one refresh with every caller in this tab (and, via Web
 * Locks, every tab). `staleAccess` is the access token the caller saw expire.
 *
 * Resolves with the access token to replay with. Rejects with an error for which
 * `isSessionEnded` is true when the session is dead, or with the raw error when the refresh got
 * no answer (keep the session).
 */
export function refreshAccessToken(refreshUrl: string, staleAccess: string | null): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = withRefreshLock((serialised) => runRefresh(refreshUrl, staleAccess, serialised)).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}
